import { useState } from 'react'
import { ArrowDown, ArrowUp, Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react'
import type { FieldDefinitionInput } from '@/api/fields'
import type { FieldDefinition, FieldOption } from '@/api/types'
import { apiErrorMessage } from './apiErrorMessage'
import {
  useAllFieldDefinitions,
  useCreateFieldDefinition,
  useDeleteFieldDefinition,
  useReorderFieldDefinitions,
  useUpdateFieldDefinition,
} from './useFieldsAdmin'

/** Full update payload derived from an existing field, with a patch applied (for hide toggles). */
function inputFromField(f: FieldDefinition, patch: Partial<FieldDefinitionInput>): FieldDefinitionInput {
  return {
    entityType: f.entityType,
    fieldKey: f.fieldKey,
    label: f.label,
    dataType: f.dataType,
    options: f.options ?? undefined,
    required: f.required,
    position: f.position,
    hidden: f.hidden,
    ...patch,
  }
}

// Every type the backend accepts (CustomFields.DATA_TYPES), grouped text-like → choice → special,
// so admins can create any field type — not just scalars — straight from this creator.
const DATA_TYPES = [
  'text', 'multiline', 'number', 'date', 'datetime', 'boolean',
  'select', 'multiselect', 'radio', 'email', 'phone', 'url', 'file',
]
/** Mirrors backend CustomFields.OPTION_TYPES — these types require an options list. */
const OPTION_TYPES = ['select', 'multiselect', 'radio']

/** Friendly names for the raw type keys (HubSpot-style), incl. types only present on seeded fields. */
const TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  multiline: 'Multi-line text',
  number: 'Number',
  date: 'Date',
  datetime: 'Date & time',
  boolean: 'Single checkbox',
  select: 'Dropdown select',
  multiselect: 'Multiple checkboxes',
  radio: 'Radio buttons',
  url: 'URL / link',
  file: 'File upload',
  email: 'Email',
  phone: 'Phone',
}

const typeLabel = (t: string): string => TYPE_LABELS[t] ?? t

const inputCls =
  'px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

/** Backend requires fieldKey to match [a-z][a-z0-9_]{0,49}. */
function toFieldKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[0-9_]+/, '')
    .replace(/_+$/, '')
    .slice(0, 50)
}

interface FieldDraft {
  fieldKey: string
  label: string
  dataType: string
  required: boolean
  options: FieldOption[]
}

function draftFrom(f?: FieldDefinition): FieldDraft {
  return {
    fieldKey: f?.fieldKey ?? '',
    label: f?.label ?? '',
    dataType: f?.dataType ?? 'text',
    required: f?.required ?? false,
    options: f?.options?.map((o) => ({ value: o.value, label: o.label })) ?? [],
  }
}

function FieldEditor({
  initial,
  keyEditable,
  pending,
  error,
  onSave,
  onCancel,
}: {
  initial: FieldDraft
  keyEditable: boolean
  pending: boolean
  error: string | null
  onSave: (d: FieldDraft) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(initial)
  // Existing fields may use types beyond the editable set (e.g. seeded email/multiselect) — keep them selectable.
  const types = DATA_TYPES.includes(draft.dataType) ? DATA_TYPES : [...DATA_TYPES, draft.dataType]
  const needsOptions = OPTION_TYPES.includes(draft.dataType)

  function patchOption(i: number, patch: Partial<FieldOption>) {
    setDraft((d) => ({ ...d, options: d.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) }))
  }

  return (
    <div className="px-4 py-3 bg-gray-50/60 border-t border-gray-100 space-y-2">
      <div className="flex items-center gap-2">
        <input
          className={`${inputCls} flex-1`}
          placeholder="Label"
          value={draft.label}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              label: e.target.value,
              // The key is derived from the label while creating; it is fixed after creation.
              fieldKey: keyEditable ? toFieldKey(e.target.value) : d.fieldKey,
            }))
          }
        />
        <span
          className="w-40 text-[11px] text-gray-400 font-mono truncate"
          title={keyEditable ? 'Key — generated from the label, fixed after creation' : 'The key is fixed after creation'}
        >
          {draft.fieldKey || 'key'}
        </span>
        <select
          className={inputCls}
          value={draft.dataType}
          onChange={(e) => setDraft((d) => ({ ...d, dataType: e.target.value }))}
        >
          {types.map((t) => (
            <option key={t} value={t}>
              {typeLabel(t)}
            </option>
          ))}
        </select>
        <label className="text-xs text-gray-500 flex items-center gap-1">
          <input
            type="checkbox"
            checked={draft.required}
            onChange={(e) => setDraft((d) => ({ ...d, required: e.target.checked }))}
          />
          required
        </label>
      </div>

      {needsOptions && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Options</span>
            <button
              type="button"
              onClick={() => setDraft((d) => ({ ...d, options: [...d.options, { value: '' }] }))}
              className="text-xs text-brand-primary flex items-center gap-1"
            >
              <Plus size={12} /> add option
            </button>
          </div>
          {draft.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                className={`${inputCls} w-40`}
                placeholder="value"
                value={o.value}
                onChange={(e) => patchOption(i, { value: e.target.value })}
              />
              <input
                className={`${inputCls} flex-1`}
                placeholder="Label (optional)"
                value={o.label ?? ''}
                onChange={(e) => patchOption(i, { label: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setDraft((d) => ({ ...d, options: d.options.filter((_, j) => j !== i) }))}
                className="text-gray-300 hover:text-danger"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {draft.options.length === 0 && (
            <p className="text-[11px] text-gray-400">A {draft.dataType} field needs at least one option.</p>
          )}
        </div>
      )}

      {error && <p className="text-danger text-xs">{error}</p>}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          disabled={pending || !draft.label.trim() || !draft.fieldKey.trim()}
          onClick={() => onSave(draft)}
          className="px-3 py-1.5 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
        >
          {pending ? 'Saving…' : 'Save field'}
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-500 hover:text-brand-dark">
          Cancel
        </button>
      </div>
    </div>
  )
}

function EntityFieldsCard({
  entityType,
  title,
  description,
  fields,
  query,
  sortMode,
}: {
  entityType: 'contact' | 'deal'
  title: string
  description: string
  fields: FieldDefinition[]
  query: string
  /** 'manual' = stored position (reorderable); 'az' = alphabetical by label (reorder disabled). */
  sortMode: 'manual' | 'az'
}) {
  const create = useCreateFieldDefinition()
  const update = useUpdateFieldDefinition()
  const del = useDeleteFieldDefinition()
  const reorder = useReorderFieldDefinitions()

  // null = closed, 'new' = creating, otherwise the id being edited
  const [editing, setEditing] = useState<string | null>(null)

  function open(id: string | null) {
    create.reset()
    update.reset()
    setEditing(id)
  }

  function toInput(d: FieldDraft, existing?: FieldDefinition): FieldDefinitionInput {
    const options = OPTION_TYPES.includes(d.dataType)
      ? d.options
          .filter((o) => o.value.trim())
          .map((o) => ({ value: o.value.trim(), ...(o.label?.trim() ? { label: o.label.trim() } : {}) }))
      : undefined
    return {
      entityType,
      fieldKey: existing?.fieldKey ?? d.fieldKey.trim(),
      label: d.label.trim(),
      dataType: d.dataType,
      options,
      required: d.required,
      position: existing?.position ?? fields.reduce((m, f) => Math.max(m, f.position), -1) + 1,
      hidden: existing?.hidden, // preserve visibility when editing; new fields default to visible
    }
  }

  function save(d: FieldDraft) {
    if (editing === 'new') {
      create.mutate(toInput(d), { onSuccess: () => setEditing(null) })
    } else if (editing) {
      const existing = fields.find((f) => f.id === editing)
      update.mutate({ id: editing, input: toInput(d, existing) }, { onSuccess: () => setEditing(null) })
    }
  }

  function remove(f: FieldDefinition) {
    if (!window.confirm(`Delete field “${f.label}”? Stored values will no longer be shown.`)) return
    del.mutate(f.id)
  }

  const saveError = create.error ?? update.error
  const delError = del.error

  // Manual = stored `position` (what admins reorder and what the forms render); A–Z sorts by label.
  // Reordering only makes sense in manual order and with no text filter (order is ambiguous otherwise).
  const q = query.trim().toLowerCase()
  const reorderable = sortMode === 'manual' && !q
  const visible = [...fields]
    .sort((a, b) => (sortMode === 'az' ? a.label.localeCompare(b.label) : a.position - b.position))
    .filter((f) => !q || f.label.toLowerCase().includes(q) || f.fieldKey.toLowerCase().includes(q))

  function move(i: number, dir: -1 | 1) {
    if (!reorderable) return
    const ids = visible.map((x) => x.id)
    const j = i + dir
    if (j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    reorder.mutate({ entityType, ids })
  }

  function toggleHidden(f: FieldDefinition) {
    update.mutate({ id: f.id, input: inputFromField(f, { hidden: !f.hidden }) })
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-brand-darkest">{title}</div>
          <div className="text-xs text-gray-500">{description}</div>
        </div>
        <button
          onClick={() => open('new')}
          className="text-xs text-brand-primary flex items-center gap-1 shrink-0"
        >
          <Plus size={12} /> add field
        </button>
      </div>

      {visible.length === 0 && editing !== 'new' && (
        <p className="px-4 pb-3 text-xs text-gray-400">
          {fields.length === 0 ? 'No custom fields yet.' : 'No fields match your search.'}
        </p>
      )}

      {visible.map((f, i) =>
        editing === f.id ? (
          <FieldEditor
            key={f.id}
            initial={draftFrom(f)}
            keyEditable={false}
            pending={update.isPending}
            error={saveError ? apiErrorMessage(saveError) : null}
            onSave={save}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <div
            key={f.id}
            className={`px-4 py-2.5 border-t border-gray-100 flex items-center gap-2 ${f.hidden ? 'opacity-50' : ''}`}
          >
            <div className="flex flex-col leading-none">
              <button
                onClick={() => move(i, -1)}
                disabled={!reorderable || i === 0}
                className="text-gray-300 hover:text-brand-primary disabled:opacity-30"
                title="Move up"
              >
                <ArrowUp size={12} />
              </button>
              <button
                onClick={() => move(i, 1)}
                disabled={!reorderable || i === visible.length - 1}
                className="text-gray-300 hover:text-brand-primary disabled:opacity-30"
                title="Move down"
              >
                <ArrowDown size={12} />
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-sm text-brand-dark">{f.label}</span>
              <span className="ml-2 text-xs text-gray-400 font-mono">{f.fieldKey}</span>
            </div>
            <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{typeLabel(f.dataType)}</span>
            {f.required && (
              <span className="text-xs bg-brand-primary/10 text-brand-primary rounded-full px-2 py-0.5">required</span>
            )}
            {f.fromTemplate && (
              <span
                className="text-xs bg-brand-accent/20 text-brand-dark rounded-full px-2 py-0.5"
                title="Preset: this field came ready-made with your industry template (not added by your team). You can still edit, hide, reorder or delete it like any custom field."
              >
                preset
              </span>
            )}
            <button
              onClick={() => toggleHidden(f)}
              className="text-gray-400 hover:text-brand-primary"
              title={f.hidden ? 'Show on forms' : 'Hide from forms'}
            >
              {f.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button onClick={() => open(f.id)} className="text-gray-400 hover:text-brand-primary">
              <Pencil size={14} />
            </button>
            <button onClick={() => remove(f)} className="text-gray-400 hover:text-danger">
              <Trash2 size={14} />
            </button>
          </div>
        ),
      )}

      {editing === 'new' && (
        <FieldEditor
          initial={draftFrom()}
          keyEditable
          pending={create.isPending}
          error={saveError ? apiErrorMessage(saveError) : null}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      {delError && <p className="px-4 py-2 text-danger text-xs border-t border-gray-100">{apiErrorMessage(delError)}</p>}
    </div>
  )
}

/** {@code only} scopes the tab to one entity — e.g. "Manage fields" opened from a deal shows only
 *  deal fields (contact custom fields don't appear on a deal, so listing them there is noise). */
export default function FieldsTab({ only }: { only?: 'contact' | 'deal' } = {}) {
  const { data, isLoading } = useAllFieldDefinitions()
  const all = data ?? []
  const [query, setQuery] = useState('')
  const [sortMode, setSortMode] = useState<'manual' | 'az'>('manual')

  return (
    <div className="max-w-5xl space-y-6">
      <p className="text-sm text-gray-500">
        Custom fields appear on {only === 'deal' ? 'deal' : only === 'contact' ? 'contact' : 'contact and deal'} forms.
        The key identifies the field in stored data and cannot be changed later.
      </p>
      {!isLoading && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search fields…"
            className={`${inputCls} flex-1 max-w-xs`}
          />
          {/* A–Z helps find a field among many; Manual keeps the drag order used on forms. */}
          <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs">
            {(['manual', 'az'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setSortMode(m)}
                className={`px-2.5 py-1.5 ${sortMode === m ? 'bg-brand-primary text-white' : 'bg-white text-gray-600 hover:text-brand-dark'}`}
              >
                {m === 'manual' ? 'Manual order' : 'A–Z'}
              </button>
            ))}
          </div>
        </div>
      )}
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading fields…</p>
      ) : (
        <>
          {only !== 'deal' && (
            <EntityFieldsCard
              entityType="contact"
              title="Contact fields"
              description="Extra fields on every contact."
              fields={all.filter((f) => f.entityType === 'contact')}
              query={query}
              sortMode={sortMode}
            />
          )}
          {only !== 'contact' && (
            <EntityFieldsCard
              entityType="deal"
              title="Deal fields"
              description="Extra fields on every deal."
              fields={all.filter((f) => f.entityType === 'deal')}
              query={query}
              sortMode={sortMode}
            />
          )}
        </>
      )}
    </div>
  )
}
